import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useReviews, faceOf, dateText, hueOf, initialOf, waitText, MAX_MESSAGE, QUICK_PHRASES, STATUS_LABEL } from "../reviewsLogic";
import { Empty, Skeleton } from "../ui";

/*
 * หน้า "รีวิวลูกค้า" — ดีไซน์ landing-ver2 (แดชบอร์ดคะแนนนีออน): วงแหวนเรืองแสงแสดงคะแนนเฉลี่ย · แท่งกระจายดาว · รีวิวเด่นเลื่อนแนวนอน · ฟีดรีวิวมีรางสีตามดาว
 * ช่องเขียนรีวิวกางในหน้า (ไม่ใช่แผ่นเด้ง) — ตรรกะอยู่ที่ reviewsLogic.js ร่วมกับ landing ซึ่งใช้ดีไซน์กำแพงคำชมแทน
 */

const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

function Stars({ value, size = 15 }) {
  return (
    <span className="rvn-stars" role="img" aria-label={`${value} ดาว`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon key={n} icon="solar:star-bold" width={size} height={size} className={n <= value ? "on" : ""} />
      ))}
    </span>
  );
}

function Composer({ busy, result, submit, onSent, onCancel }) {
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const f = faceOf(rating);
  const addPhrase = (p) => setMessage((m) => (m.includes(p) ? m : `${m}${m && !/\s$/.test(m) ? " " : ""}${p}`).slice(0, MAX_MESSAGE));
  const send = async () => {
    if (await submit(rating, message)) onSent();
  };
  return (
    <div className="rvn-composer" style={{ "--rv": f.color }}>
      <div className="rvn-comp-head">
        <Icon icon={f.icon} width={44} height={44} key={rating} className="rvn-comp-face" />
        <div>
          <b>ให้คะแนน · {f.label}</b>
          <div className="rvn-pick" role="radiogroup" aria-label="ให้คะแนน">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} ดาว`} className={n <= rating ? "on" : ""} onClick={() => setRating(n)}>
                <Icon icon="solar:star-bold" width={30} height={30} />
              </button>
            ))}
          </div>
        </div>
      </div>
      <textarea rows={4} maxLength={MAX_MESSAGE} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="เล่าประสบการณ์ของคุณ (ไม่บังคับ)" aria-label="ข้อความรีวิว" />
      <div className="rvn-comp-foot">
        <div className="rvn-phrases">
          {QUICK_PHRASES.map((p) => (
            <button key={p} type="button" onClick={() => addPhrase(p)}>
              + {p}
            </button>
          ))}
        </div>
        <small>
          {message.length}/{MAX_MESSAGE}
        </small>
      </div>
      {result && !result.ok && (
        <div className="rvn-err" role="alert">
          <Icon icon="solar:danger-triangle-bold" width={18} />
          <span>
            <b>{result.message}</b> {result.wait_seconds > 0 ? `· ${waitText(result.wait_seconds)}` : ""}
          </span>
        </div>
      )}
      <div className="rvn-comp-actions">
        <span>
          <Icon icon="solar:shield-check-bold" width={15} /> แสดงหลังแอดมินตรวจสอบ · ชื่อถูกปิดบังบางส่วน
        </span>
        <button type="button" className="rvn-ghost" onClick={onCancel} disabled={busy}>
          ยกเลิก
        </button>
        <button type="button" className="rvn-btn" onClick={send} disabled={busy}>
          <Icon icon={busy ? "svg-spinners:ring-resize" : "solar:plain-bold"} width={18} />
          {busy ? "กำลังส่ง..." : "ส่งรีวิว"}
        </button>
      </div>
    </div>
  );
}

function FeedRow({ r }) {
  const f = faceOf(r.rating);
  return (
    <article className="rvn-row" style={{ "--rv": f.color }}>
      <div className="rvn-av" style={{ "--h": hueOf(r.name) }} aria-hidden="true">
        {initialOf(r.name)}
      </div>
      <div className="rvn-row-main">
        <div className="rvn-row-top">
          <b>{r.name}</b>
          <Stars value={r.rating} />
          <time>{dateText(r.created_at)}</time>
        </div>
        {r.message ? <p>{r.message}</p> : <p className="mute">ให้ {r.rating} ดาว</p>}
      </div>
      <Icon icon={f.icon} width={30} height={30} className="rvn-row-face" />
    </article>
  );
}

export default function ReviewsPage() {
  const nav = useNavigate();
  const rv = useReviews();
  const [composing, setComposing] = useState(false);
  const [sent, setSent] = useState(false);

  if (rv.loading) return <Skeleton h={320} style={{ marginTop: 18 }} />;

  const s = rv.summary;
  const pinned = rv.items.filter((r) => r.pinned);
  const feed = rv.items.filter((r) => !r.pinned);
  const write = () => {
    if (!rv.isAuthenticated) return nav("/auth");
    rv.clearResult();
    setSent(false);
    return setComposing(true);
  };
  const sentOk = () => {
    setComposing(false);
    setSent(true);
  };
  const dash = (Math.max(0, Math.min(5, s.avg)) / 5) * RING_C;

  return (
    <div className="rvn-page">
      <section className={`rvn-hero${rv.enabled ? "" : " is-off"}`}>
        <div className="rvn-glow" aria-hidden="true" />
        <div className="rvn-head">
          <span className="rvn-head-ic">
            <Icon icon="fluent-emoji-flat:speech-balloon" width={32} height={32} />
          </span>
          <div>
            <h1>รีวิวลูกค้า</h1>
            <p>เสียงจากสมาชิกที่ใช้งานจริง</p>
          </div>
          <span className={`rvn-led ${rv.enabled ? "on" : "off"}`}>{rv.enabled ? "ON" : "OFF"}</span>
        </div>

        <div className="rvn-score">
          <div className="rvn-ring" role="img" aria-label={`คะแนนเฉลี่ย ${s.avg} จาก 5`}>
            <svg viewBox="0 0 120 120">
              <circle className="rvn-ring-bg" cx="60" cy="60" r={RING_R} />
              <circle className="rvn-ring-fg" cx="60" cy="60" r={RING_R} strokeDasharray={`${dash} ${RING_C}`} transform="rotate(-90 60 60)" />
            </svg>
            <div className="rvn-ring-in">
              <b>{s.total ? s.avg.toFixed(1) : "–"}</b>
              <small>{s.total ? `${s.total.toLocaleString("th-TH")} รีวิว` : "ยังไม่มีรีวิว"}</small>
            </div>
          </div>
          <div className="rvn-bars">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="rvn-bar" style={{ "--rv": faceOf(n).color }}>
                <span>{n}★</span>
                <i>
                  <em style={{ width: `${s.percent[n]}%` }} />
                </i>
                <small>{s.distribution[n]}</small>
              </div>
            ))}
          </div>
        </div>

        {rv.enabled ? (
          <div className="rvn-cta-row">
            {!composing && (
              <button type="button" className="rvn-btn" onClick={write} disabled={rv.isAuthenticated && !!rv.submitBlock}>
                <Icon icon="solar:pen-new-square-bold" width={18} />
                {!rv.isAuthenticated ? "เข้าสู่ระบบเพื่อเขียนรีวิว" : rv.submitBlock ? "ส่งรีวิวแล้ว" : "เขียนรีวิว"}
              </button>
            )}
            {rv.isAuthenticated && rv.submitBlock && !composing && <span className="rvn-wait">{rv.submitBlock.message}</span>}
            {sent && <span className="rvn-ok">ส่งแล้ว! รอแอดมินตรวจสอบก่อนแสดง</span>}
          </div>
        ) : (
          <div className="rvn-off">
            <Icon icon="solar:lock-keyhole-minimalistic-bold" width={18} /> ระบบรีวิวปิดให้บริการชั่วคราว
          </div>
        )}
        {composing && rv.enabled && <Composer busy={rv.busy} result={rv.result} submit={rv.submit} onSent={sentOk} onCancel={() => setComposing(false)} />}
      </section>

      {rv.isAuthenticated && rv.mine && rv.mine.items.length > 0 && (
        <section>
          <h2 className="rvn-h2">
            <Icon icon="solar:user-check-bold-duotone" width={22} /> รีวิวของฉัน
          </h2>
          <ul className="rvn-mine">
            {rv.mine.items.slice(0, 5).map((m) => {
              const st = STATUS_LABEL[m.status] || STATUS_LABEL.PENDING;
              return (
                <li key={m.id}>
                  <Stars value={m.rating} />
                  <span className="rvn-mine-t">{m.shown_message || m.message || "ให้ดาวอย่างเดียว"}</span>
                  <time>{dateText(m.created_at)}</time>
                  <span className={`rvn-chip ${st.tone}`}>{st.label}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {rv.enabled && pinned.length > 0 && (
        <section>
          <h2 className="rvn-h2">
            <Icon icon="solar:medal-star-bold-duotone" width={22} /> รีวิวเด่น
          </h2>
          <div className="rvn-rail" role="list">
            {pinned.map((r) => {
              const f = faceOf(r.rating);
              return (
                <article key={r.id} role="listitem" className="rvn-feat" style={{ "--rv": f.color }}>
                  <div className="rvn-feat-top">
                    <Icon icon={f.icon} width={36} height={36} />
                    <div>
                      <b>{r.name}</b>
                      <Stars value={r.rating} />
                    </div>
                    <Icon icon="fluent-emoji-flat:pushpin" width={20} height={20} className="rvn-pin" />
                  </div>
                  <p>{r.message || `ให้ ${r.rating} ดาว`}</p>
                  <time>{dateText(r.created_at)}</time>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {rv.enabled && (
        <section>
          <h2 className="rvn-h2">
            <Icon icon="solar:chat-round-like-bold-duotone" width={22} /> รีวิวล่าสุด
          </h2>
          {rv.items.length === 0 ? (
            <div className="rvn-card">
              <Empty icon="solar:chat-round-like-bold-duotone">ยังไม่มีรีวิวที่แสดง</Empty>
            </div>
          ) : (
            <div className="rvn-feed">
              {feed.map((r) => (
                <FeedRow key={r.id} r={r} />
              ))}
              {feed.length === 0 && <p className="rvn-note">รีวิวทั้งหมดอยู่ในส่วน “รีวิวเด่น” ด้านบน</p>}
            </div>
          )}
          {rv.hasMore && (
            <button type="button" className="rvn-more" onClick={rv.loadMore} disabled={rv.more}>
              <Icon icon={rv.more ? "svg-spinners:ring-resize" : "solar:alt-arrow-down-bold"} width={18} />
              {rv.more ? "กำลังโหลด..." : "ดูรีวิวเพิ่ม"}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
